import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useWebSocket } from '@/hooks/useWebSocket';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '@/components/ui/tooltip';
import AddLeadDialog from '@/components/AddLeadDialog';
import CallHistorySheet from '@/components/CallHistorySheet';
import SmsComposeDialog from '@/components/SmsComposeDialog';
import { toast } from 'sonner';
import {
  Phone, Plus, Search, LogOut, Smartphone,
  MoreVertical, History, PhoneCall, Settings, MessageSquare
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  new: { label: 'Neu', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  pending: { label: 'Ausstehend', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  reached: { label: 'Erreicht', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  not_reached: { label: 'Nicht erreicht', cls: 'bg-red-50 text-red-700 border-red-200' },
  mailbox: { label: 'Mailbox', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  callback: { label: 'Rückruf', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  no_interest: { label: 'Kein Interesse', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_MAP[status] || STATUS_MAP.new;
  return (
    <Badge variant="outline" className={`rounded-sm text-xs ${cfg.cls}`} data-testid={`status-badge-${status}`}>
      {cfg.label}
    </Badge>
  );
}

function formatTime(iso) {
  if (!iso) return '–';
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) +
    ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

export default function DashboardPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();

  // Redirect mobile devices to /mobile
  useEffect(() => {
    const isMobile = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile) {
      navigate('/mobile', { replace: true });
    }
  }, [navigate]);

  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [deviceCount, setDeviceCount] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [historyLead, setHistoryLead] = useState(null);
  const [smsLead, setSmsLead] = useState(null);
  const [loadingCall, setLoadingCall] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };
  const { connected, lastMessage } = useWebSocket(token);

  useEffect(() => {
    if (!lastMessage) return;
    if (lastMessage.type === 'device_count') setDeviceCount(lastMessage.count);
    if (lastMessage.type === 'call_status_updated') fetchLeads();
  }, [lastMessage]);

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${API}/leads`, { headers });
      setLeads(res.data);
    } catch {
      toast.error('Fehler beim Laden der Leads');
    }
  };

  useEffect(() => { fetchLeads(); }, []);

  const triggerCall = async (lead) => {
    setLoadingCall(lead.id);
    try {
      await axios.post(`${API}/calls/trigger`, {
        lead_id: lead.id, phone_number: lead.phone, lead_name: lead.name
      }, { headers });
      toast.success(`Anruf an Handy gesendet: ${lead.name}`);
      if (!mobileConnected) {
        toast.info('Tipp: Öffne /mobile auf deinem Handy für sofortige Anrufe.');
      }
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setLoadingCall(null);
    }
  };

  const deleteLead = async (lid) => {
    try {
      await axios.delete(`${API}/leads/${lid}`, { headers });
      toast.success('Lead gelöscht');
      fetchLeads();
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  const filtered = leads.filter(l =>
    l.name.toLowerCase().includes(search.toLowerCase()) ||
    l.phone.includes(search)
  );

  const mobileConnected = deviceCount >= 2;

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
        {/* Top Bar */}
        <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-black rounded-sm flex items-center justify-center">
                <Phone className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                CallRelay
              </span>
            </div>
            <Separator orientation="vertical" className="h-6" />
            <div className="flex items-center gap-2" data-testid="device-status">
              <div className={`w-2 h-2 rounded-full ${mobileConnected ? 'bg-emerald-500' : 'bg-slate-300'}`} />
              <span className="text-sm text-slate-500">
                {mobileConnected ? 'Handy verbunden' : 'Kein Handy verbunden'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/mobile"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1.5 transition-colors"
              data-testid="open-mobile-link"
            >
              <Smartphone className="w-4 h-4" />
              <span className="hidden sm:inline">Mobile</span>
            </a>
            <a
              href="/settings"
              className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1.5 transition-colors"
              data-testid="open-settings-link"
            >
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Einstellungen</span>
            </a>
            <Separator orientation="vertical" className="h-6" />
            <span className="text-sm font-medium text-slate-700" data-testid="user-name">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-button">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </header>

        {/* Main */}
        <main className="p-6 max-w-7xl mx-auto">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Leads
              </h2>
              <Badge variant="secondary" className="rounded-sm">{leads.length}</Badge>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-none">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Name oder Telefon suchen..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 w-full sm:w-64 rounded-sm"
                  data-testid="search-input"
                />
              </div>
              <Button
                onClick={() => setAddOpen(true)}
                className="rounded-sm bg-black text-white hover:bg-slate-800 shrink-0"
                data-testid="add-lead-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Lead hinzufügen
              </Button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="leads-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left text-xs uppercase tracking-wider font-bold text-slate-500 py-3 px-4">Name</th>
                    <th className="text-left text-xs uppercase tracking-wider font-bold text-slate-500 py-3 px-4">Telefon</th>
                    <th className="text-left text-xs uppercase tracking-wider font-bold text-slate-500 py-3 px-4 hidden md:table-cell">Position</th>
                    <th className="text-left text-xs uppercase tracking-wider font-bold text-slate-500 py-3 px-4">Status</th>
                    <th className="text-left text-xs uppercase tracking-wider font-bold text-slate-500 py-3 px-4 hidden lg:table-cell">Letzter Anruf</th>
                    <th className="text-right text-xs uppercase tracking-wider font-bold text-slate-500 py-3 px-4">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(lead => (
                    <tr
                      key={lead.id}
                      className="border-b border-slate-200 hover:bg-slate-50 transition-colors"
                      data-testid={`lead-row-${lead.id}`}
                    >
                      <td className="py-4 px-4 font-medium text-slate-900">{lead.name}</td>
                      <td className="py-4 px-4 font-mono text-sm text-slate-700">{lead.phone}</td>
                      <td className="py-4 px-4 text-sm text-slate-500 hidden md:table-cell">{lead.position || '–'}</td>
                      <td className="py-4 px-4">
                        <StatusBadge status={lead.last_call?.status || lead.status} />
                      </td>
                      <td className="py-4 px-4 text-sm text-slate-500 hidden lg:table-cell">
                        {formatTime(lead.last_call?.created_at)}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end gap-1.5">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-8 w-8 p-0"
                                onClick={() => triggerCall(lead)}
                                disabled={loadingCall === lead.id}
                                data-testid={`call-button-${lead.id}`}
                              >
                                {loadingCall === lead.id ? (
                                  <PhoneCall className="w-4 h-4 animate-pulse" />
                                ) : (
                                  <Phone className="w-4 h-4" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="left">
                              <p>Anruf ans Handy senden</p>
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm h-8 w-8 p-0"
                                onClick={() => setSmsLead(lead)}
                                data-testid={`sms-button-${lead.id}`}
                              >
                                <MessageSquare className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="left">
                              <p>SMS ans Handy senden</p>
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0"
                                onClick={() => setHistoryLead(lead)}
                                data-testid={`history-button-${lead.id}`}
                              >
                                <History className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="left">
                              <p>Anrufverlauf</p>
                            </TooltipContent>
                          </Tooltip>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0"
                                data-testid={`lead-menu-${lead.id}`}
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="rounded-sm">
                              <DropdownMenuItem
                                onClick={() => setEditLead(lead)}
                                data-testid={`edit-lead-${lead.id}`}
                              >
                                Bearbeiten
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-600 focus:text-red-600"
                                onClick={() => deleteLead(lead.id)}
                                data-testid={`delete-lead-${lead.id}`}
                              >
                                Löschen
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filtered.length === 0 && (
              <div className="py-16 text-center" data-testid="empty-leads">
                <Phone className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">
                  {search ? 'Keine Leads gefunden' : 'Noch keine Leads vorhanden. Erstelle deinen ersten Lead.'}
                </p>
              </div>
            )}
          </div>

          {/* Mobile hint */}
          {!mobileConnected && leads.length > 0 && (
            <div className="mt-4 bg-white border border-amber-200 rounded-sm p-4 flex items-start gap-3" data-testid="mobile-hint">
              <Smartphone className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-slate-900">Handy verbinden</p>
                <p className="text-sm text-slate-500 mt-0.5">
                  Öffne{' '}
                  <code className="bg-slate-100 px-1.5 py-0.5 rounded-sm text-xs font-mono">
                    {window.location.origin}/mobile
                  </code>{' '}
                  auf deinem Handy und melde dich an, um Anrufe zu empfangen.
                </p>
              </div>
            </div>
          )}
        </main>

        {/* Dialogs */}
        <AddLeadDialog
          open={addOpen || !!editLead}
          onClose={() => { setAddOpen(false); setEditLead(null); }}
          lead={editLead}
          onSaved={fetchLeads}
        />
        <CallHistorySheet
          lead={historyLead}
          onClose={() => setHistoryLead(null)}
        />
        <SmsComposeDialog
          open={!!smsLead}
          onClose={() => setSmsLead(null)}
          lead={smsLead}
        />
      </div>
    </TooltipProvider>
  );
}
